import React, { useState } from 'react';
import { Plus, Minus, Moon, Sun, LogOut, MapPin, Mail, ChevronDown, Terminal, Settings2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from './ThemeContext';
interface ManagementScreenProps {
  city: string;
  email: string;
  onLogout: () => void;
  onChangeCity: () => void;
}
export function ManagementScreen({
  city,
  email,
  onLogout,
  onChangeCity
}: ManagementScreenProps) {
  const [evaluatePhotos, setEvaluatePhotos] = useState(false);
  const {
    theme,
    toggleTheme
  } = useTheme();
  const [questions, setQuestions] = useState<string[]>([]);
  const [logsOpen, setLogsOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [mode, setMode] = useState<'male' | 'female' | 'standard'>('standard');
  const modes = [{
    id: 'male' as const,
    title: 'Мужчина',
    description: 'Бот пишет кандидатам более активно и проявляет инициативу.'
  }, {
    id: 'female' as const,
    title: 'Женщина',
    description: 'Бот более пассивен в диалогах. Предпочитает отвечать на вопросы, а не задавать их. Не пишет первым.'
  }, {
    id: 'standard' as const,
    title: 'Стандартный',
    description: 'Среднее между режимами. Бот пишет первым и иногда задаёт вопросы.'
  }];
  const addQuestion = () => {
    setQuestions([...questions, '']);
  };
  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };
  return <div className="min-h-screen bg-svt-bg" data-id="element-199">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-svt-bg/80 backdrop-blur-xl border-b border-svt-border transition-colors duration-300" data-id="element-200">
        <div className="flex items-center justify-between px-6 py-4" data-id="element-201">
          <div className="flex items-center gap-3 min-w-0" data-id="element-202">
            <img src="https://cdn.magicpatterns.com/uploads/x3zgz2YSJzc57ZhT1dBqrP/logo.png" alt="SVT Dating" className="h-8 w-auto flex-shrink-0" data-id="element-203" />
            <span className="font-serif text-xl font-bold tracking-wide text-svt-text" data-id="element-204">
              Управление
            </span>
          </div>
          <div className="flex items-center bg-svt-overlay rounded-full p-1 border border-svt-border" data-id="element-205">
            <button onClick={theme === 'light' ? toggleTheme : undefined} className={`p-1.5 rounded-full transition-all duration-300 ${theme === 'dark' ? 'bg-gradient-primary text-white shadow-md' : 'text-svt-text-muted hover:text-svt-text'}`} data-id="element-206">
              <Moon size={14} data-id="element-207" />
            </button>
            <button onClick={theme === 'dark' ? toggleTheme : undefined} className={`p-1.5 rounded-full transition-all duration-300 ${theme === 'light' ? 'bg-gradient-primary text-white shadow-md' : 'text-svt-text-muted hover:text-svt-text'}`} data-id="element-208">
              <Sun size={14} data-id="element-209" />
            </button>
          </div>
        </div>

        {/* Account row: email + inline logout icon */}
        <div className="px-6 pb-3" data-id="element-210">
          <div className="flex items-center gap-2 min-w-0 px-3 py-2 rounded-xl bg-svt-surface border border-svt-border" data-id="element-211">
            <Mail size={14} className="text-svt-pink flex-shrink-0" data-id="element-212" />
            <span className="text-sm text-svt-text-muted flex-shrink-0" data-id="element-213">
              Аккаунт:
            </span>
            <span className="text-sm font-medium text-svt-text truncate font-mono flex-1" data-id="element-214">
              {email}
            </span>
            <button onClick={onLogout} aria-label="Выйти" title="Выйти" className="p-1.5 rounded-lg text-svt-text-muted hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0" data-id="element-215">
              <LogOut size={16} data-id="element-216" />
            </button>
          </div>
        </div>

        {/* Divider */}
        <div className="px-6" data-id="element-217">
          <div className="h-px bg-svt-border" data-id="element-218"></div>
        </div>

        {/* Bot context row: city + bot status (configuration & state) */}
        <div className="px-6 py-3 flex items-stretch gap-3" data-id="element-219">
          <button onClick={onChangeCity} className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-xl bg-svt-surface border border-svt-border hover:bg-svt-surface-hover transition-colors" data-id="element-220">
            <MapPin size={14} className="text-svt-pink flex-shrink-0" data-id="element-221" />
            <span className="text-sm text-svt-text-muted flex-shrink-0" data-id="element-222">
              Город:
            </span>
            <span className="text-sm font-medium text-svt-text truncate" data-id="element-223">
              {city}
            </span>
          </button>
          <div className="flex items-center justify-center flex-shrink-0 px-4 rounded-xl bg-red-500/5 border border-red-500/20" data-id="element-224">
            <span className="text-sm font-semibold text-red-400" data-id="element-225">Выключен</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 space-y-6" data-id="element-226">
        {/* ТРЕБОВАНИЯ К КАНДИДАТУ */}
        <div data-id="element-227">
          <label className="block text-xs font-bold tracking-widest uppercase text-svt-text-muted mb-3" data-id="element-228">
            Требования к кандидату
          </label>
          <textarea rows={6} placeholder="Опишите требования к кандидату..." className="w-full px-4 py-3 rounded-2xl bg-svt-surface border border-svt-border text-svt-text text-sm leading-relaxed placeholder:text-svt-text-faint focus:outline-none focus:border-svt-pink/50 transition-colors resize-none" data-id="element-229" />
        </div>

        {/* ВОПРОСЫ КАНДИДАТУ */}
        <div data-id="element-230">
          <div className="flex items-center justify-between mb-3" data-id="element-231">
            <label className="text-xs font-bold tracking-widest uppercase text-svt-text-muted" data-id="element-232">
              Вопросы кандидату
            </label>
            <button onClick={addQuestion} className="w-8 h-8 rounded-lg bg-svt-surface border border-svt-border hover:bg-svt-surface-hover transition-colors flex items-center justify-center" data-id="element-233">
              <Plus size={16} className="text-svt-text-muted" data-id="element-234" />
            </button>
          </div>
          <div className="space-y-3" data-id="element-235">
            {questions.length === 0 ? <p className="text-sm text-svt-text-faint italic px-4 py-3" data-id="element-236">
                Нажмите + чтобы добавить вопрос
              </p> : questions.map((question, index) => <div key={index} className="flex gap-3" data-id="element-237">
                  <textarea rows={2} placeholder="Введите вопрос..." defaultValue={question} className="flex-1 p-4 rounded-2xl bg-svt-surface border border-svt-border text-sm text-svt-text leading-relaxed placeholder:text-svt-text-faint focus:outline-none focus:border-svt-pink/50 transition-colors resize-none" data-id="element-238" />
                  <button onClick={() => removeQuestion(index)} className="w-10 h-10 flex-shrink-0 rounded-lg bg-svt-surface border border-svt-border hover:bg-svt-surface-hover transition-colors flex items-center justify-center" data-id="element-239">
                    <Minus size={16} className="text-svt-text-muted" data-id="element-240" />
                  </button>
                </div>)}
          </div>
        </div>

        {/* РЕЖИМЫ РАБОТЫ */}
        <div data-id="element-241">
          <label className="block text-xs font-bold tracking-widest uppercase text-svt-text-muted mb-3" data-id="element-242">
            Режимы работы
          </label>
          <div className="space-y-3" data-id="element-243">
            {modes.map(m => {
            const isSelected = mode === m.id;
            return <button key={m.id} onClick={() => setMode(m.id)} className={`w-full text-left p-4 rounded-2xl border transition-all duration-200 ${isSelected ? 'bg-svt-card-highlight border-svt-pink/40 shadow-[0_0_20px_rgba(232,67,147,0.1)]' : 'bg-svt-surface border-svt-border hover:border-svt-border-hover'}`} data-id="element-244">
                  <div className="flex items-start gap-3" data-id="element-245">
                    {/* Radio indicator */}
                    <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${isSelected ? 'border-svt-pink' : 'border-svt-border'}`} data-id="element-246">
                      {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-gradient-primary" data-id="element-247"></div>}
                    </div>
                    <div className="min-w-0" data-id="element-248">
                      <p className={`text-sm font-semibold mb-1 ${isSelected ? 'text-svt-text' : 'text-svt-text-secondary'}`} data-id="element-249">
                        {m.title}
                      </p>
                      <p className="text-xs text-svt-text-muted leading-relaxed" data-id="element-250">
                        {m.description}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>
        </div>

        {/* Запустить Button */}
        <button className="w-full py-4 rounded-full bg-green-500 hover:bg-green-600 text-white font-bold text-lg transition-all shadow-[0_0_20px_rgba(34,197,94,0.3)] hover:shadow-[0_0_30px_rgba(34,197,94,0.5)]" data-id="element-251">
          Запустить
        </button>

        {/* ПРОДВИНУТЫЕ НАСТРОЙКИ — collapsible */}
        <div data-id="element-252">
          <button onClick={() => setAdvancedOpen(!advancedOpen)} className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-svt-surface border border-svt-border hover:bg-svt-surface-hover transition-colors group" data-id="element-253">
            <div className="flex items-center gap-2.5" data-id="element-254">
              <Settings2 size={14} className="text-svt-pink" data-id="element-255" />
              <span className="text-xs font-bold tracking-widest uppercase text-svt-text-muted" data-id="element-256">
                Продвинутые настройки
              </span>
            </div>
            <motion.div animate={{
            rotate: advancedOpen ? 180 : 0
          }} transition={{
            duration: 0.2
          }} data-id="element-257">
              <ChevronDown size={16} className="text-svt-text-muted" data-id="element-258" />
            </motion.div>
          </button>

          <AnimatePresence initial={false} data-id="element-259">
            {advancedOpen && <motion.div key="advanced-content" initial={{
            height: 0,
            opacity: 0,
            marginTop: 0
          }} animate={{
            height: 'auto',
            opacity: 1,
            marginTop: 8
          }} exit={{
            height: 0,
            opacity: 0,
            marginTop: 0
          }} transition={{
            duration: 0.25,
            ease: 'easeInOut'
          }} className="overflow-hidden" data-id="element-260">
                <div className="p-4 rounded-2xl bg-svt-surface border border-svt-border" data-id="element-261">
                  <button onClick={() => setEvaluatePhotos(!evaluatePhotos)} className="flex items-center gap-3 group w-full" data-id="element-262">
                    <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all flex-shrink-0 ${evaluatePhotos ? 'bg-green-500 border-green-500' : 'border-svt-border group-hover:border-svt-border-hover'}`} data-id="element-263">
                      {evaluatePhotos && <svg className="w-4 h-4 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor" data-id="element-264">
                          <path d="M5 13l4 4L19 7" data-id="element-265"></path>
                        </svg>}
                    </div>
                    <span className="text-sm font-medium text-svt-text text-left" data-id="element-266">
                      Оценивать фото профиля
                    </span>
                  </button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* ЛОГИ — collapsible */}
        <div data-id="element-267">
          <button onClick={() => setLogsOpen(!logsOpen)} className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-svt-surface border border-svt-border hover:bg-svt-surface-hover transition-colors group" data-id="element-268">
            <div className="flex items-center gap-2.5" data-id="element-269">
              <Terminal size={14} className="text-svt-pink" data-id="element-270" />
              <span className="text-xs font-bold tracking-widest uppercase text-svt-text-muted" data-id="element-271">
                Логи
              </span>
              <span className="text-[10px] font-mono text-svt-text-faint px-1.5 py-0.5 rounded bg-svt-overlay" data-id="element-272">
                2
              </span>
            </div>
            <motion.div animate={{
            rotate: logsOpen ? 180 : 0
          }} transition={{
            duration: 0.2
          }} data-id="element-273">
              <ChevronDown size={16} className="text-svt-text-muted" data-id="element-274" />
            </motion.div>
          </button>

          <AnimatePresence initial={false} data-id="element-275">
            {logsOpen && <motion.div key="logs-content" initial={{
            height: 0,
            opacity: 0,
            marginTop: 0
          }} animate={{
            height: 'auto',
            opacity: 1,
            marginTop: 8
          }} exit={{
            height: 0,
            opacity: 0,
            marginTop: 0
          }} transition={{
            duration: 0.25,
            ease: 'easeInOut'
          }} className="overflow-hidden" data-id="element-276">
                <div className="p-4 rounded-2xl bg-svt-surface border border-svt-border font-mono text-sm space-y-2" data-id="element-277">
                  <div className="flex gap-3" data-id="element-278">
                    <span className="text-svt-pink flex-shrink-0" data-id="element-279">
                      16:23:14
                    </span>
                    <span className="text-svt-text-secondary" data-id="element-280">
                      Состояние бота изменено! Изменения применятся в течение 15
                      секунд...
                    </span>
                  </div>
                  <div className="flex gap-3" data-id="element-281">
                    <span className="text-svt-pink flex-shrink-0" data-id="element-282">
                      16:23:17
                    </span>
                    <span className="text-svt-text-secondary" data-id="element-283">
                      Запуск бота...
                    </span>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}